import { ConnectError } from '@connectrpc/connect'

import { isNetworkError } from '~/libs/isNetworkError'

/**
 * グローバル QueryClient の query retry 上限 (issue #7070 D5、PdM 承認
 * 2026-09-04)。
 */
const MAX_RETRY_COUNT = 2

/**
 * query の自動リトライ可否を判定する (issue #7070 D1/D2/R1/R2)。
 *
 * 一過性エラー (`isNetworkError` が true — `Unavailable` / `DeadlineExceeded`
 * / 実通信断) のみ、上限 `MAX_RETRY_COUNT` 回までリトライする。業務エラー
 * (`InvalidArgument` / `NotFound` 等) や `Internal` (5xx, D4 により scope
 * 外) はリトライせず即座に error state へ確定する (R2)。mutation はこの
 * predicate を使わない (R3、`defaultOptions.mutations` は未設定のまま)。
 *
 * `failureCount` は `@tanstack/react-query` の retryer が渡す実行済み失敗
 * 回数で 0 から始まる (`retryer.ts` は `retry(failureCount, error)` を
 * `failureCount++` の**前**に呼ぶ)。`error` は react-query の `DefaultError`
 * (= `Error`) 型で渡ってくるため、`ConnectError` へ絞り込むガードが必要
 * (D2)。
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    failureCount < MAX_RETRY_COUNT &&
    error instanceof ConnectError &&
    isNetworkError(error)
  )
}

/**
 * query retry の待機時間を指数 backoff で計算する (issue #7070 D5)。
 *
 * `500ms * 2^failureCount` を `4000ms` で頭打ちにする (1 回目リトライ前:
 * 500ms、2 回目リトライ前: 1000ms)。react-query 既定の `defaultRetryDelay`
 * (`retryer.ts`: `Math.min(1000 * 2 ** failureCount, 30000)`) と同じ 0-indexed
 * 指数 backoff の形を、base/cap だけ D5 の値に差し替えたもの。
 */
export function queryRetryDelay(failureCount: number): number {
  return Math.min(500 * 2 ** failureCount, 4000)
}
